import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/shared/services/auth.service';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    private readonly maxRetryInterval = 120 * 1000; // 30s
    private readonly minRetryInterval = 2 * 1000; // 2s

    private myWebSocket: WebSocketSubject<any>;

    private stateMap: Map<string, BehaviorSubject<any>> = new Map<string, BehaviorSubject<any>>();

    private auctionStatusMap: Map<string, BehaviorSubject<any>> = new Map<string, BehaviorSubject<any>>();

    constructor(private auth: AuthService) {}

    init() {
        const sessionId = this.auth.currentAuth.session_id;
        const wsUrl = environment.baseWsUrl + sessionId;
        this.myWebSocket = webSocket(wsUrl);
        this.connect(this.minRetryInterval, undefined);
    }

    close() {
        if (this.myWebSocket) {
            this.myWebSocket.unsubscribe();
        }
    }

    private connect(retryInterval, sub: Subscription) {
        if (sub) {
            sub.unsubscribe();
        }

        sub = this.myWebSocket.subscribe(
            (message) => {
                this.handleMessage(message);
            },
            (err) => {
                console.error(err);
                setTimeout(() => {
                    this.connect(Math.min(this.maxRetryInterval, retryInterval * 2), sub);
                }, retryInterval);
            },
            () => {
                console.error('WS Closed');
                setTimeout(() => {
                    this.connect(Math.min(this.maxRetryInterval, retryInterval * 2), sub);
                }, retryInterval);
            }
        );
    }

    private handleMessage(message: any) {
        if (['bid', 'counter_offer', 'decline_offer', 'accept_offer', 'buy_now'].includes(message.action)) {
            this.updateAuctionStatus(message);
        } else {
            this.updateOtherStates(message);
        }
    }

    private updateAuctionStatus(message: any) {
        const data = message.data.status;
        if (data) {
            if (this.auctionStatusMap.has(data.auction_uid)) {
                this.auctionStatusMap.get(data.auction_uid).next(data);
            } else {
                this.auctionStatusMap.set(data.auction_uid, new BehaviorSubject<any>(data));
            }
        }
    }

    private updateOtherStates(message: any) {
        if (this.stateMap.has(message.action)) {
            this.stateMap.get(message.action).next(message.data);
        } else {
            this.stateMap.set(message.action, new BehaviorSubject<any>(message.data));
        }
    }

    public getAuctionStatusObservable(auctionUid: string, latest?: any): Observable<any> {
        if (this.auctionStatusMap.has(auctionUid)) {
            this.auctionStatusMap.get(auctionUid).next(latest);
        } else {
            this.auctionStatusMap.set(auctionUid, new BehaviorSubject<any>(latest));
        }
        return this.auctionStatusMap.get(auctionUid).asObservable();
    }
}
